import type { Carer, User } from '@lmc/ts-common'
import { CommonErrors, UserRole } from '@lmc/ts-common'
import { Button, Select } from '@lmc/ui-kit'
import { Drawer } from 'antd'
import { showToast } from 'common/utils'
import useUser from 'hooks/useUser/useUser'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportsConstants } from 'locale/team'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { RoleInfoCard } from '../../../../../../components/generic/RoleInfoCard'
import {
  CapabilityKey,
  roleHasStep3,
  useRoleOptions
} from '../../../../../../components/generic/teamCapabilities'
import type { WizardRole } from '../../../../../../components/generic/teamCapabilities/types'
import { buildInitialData } from './buildInitialData'
import { buildUpdatePayload } from './buildUpdatePayload'
import { CapabilitySection } from './CapabilitySection'
import { capabilitiesForRoleChange } from './capabilitiesForRoleChange'
import SafeguardingWarningAlert, {
  type SafeguardingWarningVariant
} from './SafeguardingWarningAlert'
import {
  affectedSiteNames,
  isInsufficientSafeguardingOfficersError
} from './safeguardingError'
import type { EditPermissionsData } from './types'

export type EditPermissionsPanelProps = {
  open: boolean
  carer: Carer | null
  onClose: () => void
  // Called after a successful save so the consumer can refresh the team list
  // (the list's data is cached under a separate query key and isn't invalidated
  // by the user-update mutation).
  onSaved?: () => void
}

const PANEL_WIDTH = 411
const TEST_ID = 'edit-permissions-panel'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const UserBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const UserName = styled.div`
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const UserEmail = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FieldLabel = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const FooterRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  .ant-drawer-title {
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: ${({ theme }) => theme.color.neutral.color2};
  }

  .ant-drawer-body {
    padding: 16px;
  }
`

// TEMPORARY (CM-3452): a Privacy Officer's role can't be changed to a
// less-privileged one; the backend blocks it (its own code, with the PO homes
// as the `data` array) before the safeguarding check. Surface the same inline
// alert instead of the raw message toast. Remove once legacy privacy-officer
// roles are migrated and this error path no longer occurs.
const isPrivacyOfficerError = (err: unknown): boolean =>
  (err as { code?: number })?.code ===
  CommonErrors.Codes.privacyOfficersCannotBeUpdated

const privacyOfficerHomeNames = (err: unknown): string[] => {
  const homes = (err as { data?: Array<{ name?: string }> })?.data
  return Array.isArray(homes)
    ? homes.map((h) => h?.name).filter((n): n is string => Boolean(n))
    : []
}

// All stateful behaviour lives here so the Drawer's `footer` (pinned to the
// bottom edge of the full-height drawer) can read the Save loading state. This
// component mounts only when a carer is present and is keyed by carer.id, so
// `buildInitialData` always reflects the member just opened.
const PanelContent = ({
  open,
  carer,
  onClose,
  onClosed,
  onSaved
}: {
  open: boolean
  carer: Carer
  onClose: () => void
  onClosed: () => void
  onSaved?: () => void
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tGeneral } = useTranslation(NAMESPACE.GENERAL)

  const [data, setData] = useState<EditPermissionsData>(() =>
    buildInitialData(carer)
  )
  const [safeguardingWarning, setSafeguardingWarning] = useState<{
    variant: SafeguardingWarningVariant
    siteNames: string[]
  } | null>(null)
  // Records what the in-flight save was changing so the error handler can pick
  // the right alert variant.
  const pendingVariantRef = useRef<SafeguardingWarningVariant>('capability')

  // Re-seed whenever the drawer (re)opens. This component can stay mounted
  // through the close animation, so a quick close→reopen of the *same* member
  // would otherwise reuse this instance's state and show the cancelled edits —
  // or a stale safeguarding warning from the previous Save attempt. Clearing
  // both on each open transition guarantees a fresh start.
  const wasOpen = useRef(open)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setData(buildInitialData(carer))
      setSafeguardingWarning(null)
    }
    wasOpen.current = open
  }, [open, carer])

  const { updateUser, isLoadingUpdate } = useUser({
    id: carer.id,
    // The panel already has the carer from the list and only needs the update
    // mutation, so skip useUser's carer fetch.
    options: { enabled: false },
    onUpdateSuccess: () => {
      showToast({
        title: t(reportsConstants.EDIT_PANEL_SAVED_TOAST),
        type: 'success'
      })
      onSaved?.()
      onClose()
    },
    // The backend rejects removing a safeguarding officer when it would leave
    // ANY of the user's homes below the required minimum (a cross-home rule).
    // Show our inline contextual alert instead of the raw toast; revert the
    // pending edit so the UI reflects the unchanged server state.
    onErrorCustom: (err) => {
      if (isInsufficientSafeguardingOfficersError(err)) {
        const siteNames = affectedSiteNames(err)
        // Only treat the error as handled when the backend returned at least one
        // affected site name. If the payload is empty/malformed both alert
        // placements are guarded by siteNames.length > 0 and nothing would
        // render, leaving the edit silently reverted with no feedback. Return
        // false instead so the default error toast fires.
        if (siteNames.length === 0) {
          return false
        }
        setSafeguardingWarning({
          variant: pendingVariantRef.current,
          siteNames
        })
        // Revert the rejected edit per design (toggle back On / role back to original).
        setData(buildInitialData(carer))
        return true
      }
      // Temporary fallback (see isPrivacyOfficerError): surface the PO block as
      // the same inline alert (listing the PO homes) rather than the raw toast.
      if (isPrivacyOfficerError(err)) {
        const siteNames = privacyOfficerHomeNames(err)
        if (siteNames.length === 0) {
          return false
        }
        setSafeguardingWarning({ variant: 'privacy-officer', siteNames })
        setData(buildInitialData(carer))
        return true
      }
      return false
    }
  })

  // Any edit clears a shown warning; it's re-evaluated on the next Save attempt.
  const handleCapabilityChange = (key: CapabilityKey, next: boolean) => {
    setSafeguardingWarning(null)
    setData((d) => ({
      ...d,
      capabilities: { ...d.capabilities, [key]: next }
    }))
  }

  const handleRoleChange = (role: WizardRole) => {
    setSafeguardingWarning(null)
    setData((d) => ({
      role,
      capabilities: capabilitiesForRoleChange(d.capabilities, role)
    }))
  }

  const handleSave = () => {
    const payload = buildUpdatePayload(carer, data)
    // Record which kind of change was in flight so the error handler can pick
    // the right alert variant.
    pendingVariantRef.current =
      data.role !== buildInitialData(carer).role ? 'role' : 'capability'

    // Cast: buildUpdatePayload returns role as WizardRole (widened to include
    // 'it-security', CM-2888 pending) which the backend rejects until the enum
    // lands; UI ships per design's 6-role roster.
    updateUser(payload as Partial<User>)
  }

  // Group users (group-admin/group-manager) carry roles that aren't in the
  // single-site roster, so without the group-dashboard scope the Select has no
  // matching option and renders the raw role value (e.g. "group-admin"). Derive
  // the scope from the member being edited so the correct roster + label show.
  const isGroupUser =
    carer.role === UserRole.groupAdmin || carer.role === UserRole.groupManager
  const roleOptions = useRoleOptions(
    isGroupUser ? 'group-dashboard' : 'single-site'
  ).filter((o) => !o.disabled)

  const fullName = `${carer.name?.first ?? ''} ${carer.name?.last ?? ''}`.trim()

  // A blocked role change shows the alert under the role select AND outlines
  // the select in the danger state (per design). Both the safeguarding
  // last-officer block and the (temporary) privacy-officer block are role
  // changes, so both use this treatment.
  const roleWarningActive =
    (safeguardingWarning?.variant === 'role' ||
      safeguardingWarning?.variant === 'privacy-officer') &&
    safeguardingWarning.siteNames.length > 0
  // A blocked capability removal shows the alert under the Safeguarding
  // Oversight card AND outlines that card in the danger state (per design).
  const capabilityWarningActive =
    safeguardingWarning?.variant === 'capability' &&
    safeguardingWarning.siteNames.length > 0

  return (
    <StyledDrawer
      title={t(reportsConstants.EDIT_PANEL_TITLE)}
      placement="right"
      width={PANEL_WIDTH}
      open={open}
      onClose={onClose}
      afterOpenChange={(isOpen) => {
        if (!isOpen) {
          onClosed()
        }
      }}
      destroyOnClose
      footer={
        <FooterColumn>
          <FooterRow>
            <Button onClick={onClose}>
              {tGeneral(generalConstants.CANCEL)}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoadingUpdate}
              disabled={isLoadingUpdate}
              data-testid={`${TEST_ID}-save`}
            >
              {tGeneral(generalConstants.SAVE)}
            </Button>
          </FooterRow>
        </FooterColumn>
      }
    >
      <Body data-testid={TEST_ID}>
        <UserBlock>
          <UserName>{fullName}</UserName>
          <UserEmail>{carer.email}</UserEmail>
        </UserBlock>

        <Field>
          <FieldLabel>{t(reportsConstants.WIZARD_STEP2_ROLE)}</FieldLabel>
          <Select
            value={data.role}
            onChange={handleRoleChange}
            style={{ width: '100%' }}
            status={roleWarningActive ? 'error' : undefined}
            aria-label={t(reportsConstants.WIZARD_STEP2_ROLE)}
            options={roleOptions.map((o) => ({
              value: o.value,
              label: o.label
            }))}
            data-testid={`${TEST_ID}-role-select`}
          />
        </Field>

        {roleWarningActive && (
          <SafeguardingWarningAlert
            variant={safeguardingWarning.variant}
            siteNames={safeguardingWarning.siteNames}
            testId={`${TEST_ID}-safeguarding-warning`}
          />
        )}

        <RoleInfoCard role={data.role} collapsible defaultExpanded={false} />

        {roleHasStep3(data.role) && (
          <CapabilitySection
            role={data.role}
            capabilities={data.capabilities}
            onChange={handleCapabilityChange}
            testId={`${TEST_ID}-capabilities`}
            // Capability-variant warning sits directly below the Safeguarding
            // Oversight card (per design), not at the bottom of the section.
            renderAfter={
              capabilityWarningActive
                ? {
                    [CapabilityKey.SafeguardingOversight]: (
                      <SafeguardingWarningAlert
                        variant="capability"
                        siteNames={safeguardingWarning.siteNames}
                        testId={`${TEST_ID}-safeguarding-warning`}
                      />
                    )
                  }
                : undefined
            }
            errorKeys={
              capabilityWarningActive
                ? [CapabilityKey.SafeguardingOversight]
                : undefined
            }
          />
        )}
      </Body>
    </StyledDrawer>
  )
}

const EditPermissionsPanel = ({
  open,
  carer,
  onClose,
  onSaved
}: EditPermissionsPanelProps) => {
  // Keep the last-opened carer mounted through the Drawer's close animation so
  // the slide-out can play (the consumer clears `carer` on close). `rendered` is
  // cleared once the close transition finishes (PanelContent's afterOpenChange),
  // unmounting the panel so the next open re-initialises fresh from
  // buildInitialData. Keyed by id so switching members also re-initialises.
  const [rendered, setRendered] = useState<Carer | null>(carer)

  useEffect(() => {
    if (carer) {
      setRendered(carer)
    }
  }, [carer])

  if (!rendered) {
    return null
  }

  return (
    <PanelContent
      key={rendered.id}
      open={open}
      carer={rendered}
      onClose={onClose}
      onClosed={() => setRendered(null)}
      onSaved={onSaved}
    />
  )
}

export default EditPermissionsPanel
